const SCROLLABLE_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay'])

export const SCROLL_EPSILON = 1
const WHEEL_LINE_HEIGHT_PX = 16

const getOverflowY = (element: HTMLElement): string => {
  const { overflowY, overflow } = window.getComputedStyle(element)
  return overflowY === 'visible' ? overflow : overflowY
}

export const getMaxScrollTop = (
  element: Pick<HTMLElement, 'scrollHeight' | 'clientHeight'>,
): number => Math.max(0, element.scrollHeight - element.clientHeight)

export const canConsumeVerticalScroll = (
  element: Pick<HTMLElement, 'scrollHeight' | 'clientHeight' | 'scrollTop'>,
  deltaY: number,
  epsilon = SCROLL_EPSILON,
): boolean => {
  if (deltaY === 0) return false

  const maxScrollTop = getMaxScrollTop(element)
  if (maxScrollTop <= epsilon) return false

  return deltaY < 0
    ? element.scrollTop > epsilon
    : element.scrollTop < maxScrollTop - epsilon
}

export const getWheelDeltaYInPixels = (
  event: Pick<WheelEvent, 'deltaMode' | 'deltaY'>,
  pageHeight: number,
): number => {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_LINE:
      return event.deltaY * WHEEL_LINE_HEIGHT_PX
    case WheelEvent.DOM_DELTA_PAGE:
      return event.deltaY * pageHeight
    default:
      return event.deltaY
  }
}

export const isVerticallyScrollable = (element: HTMLElement): boolean => {
  return (
    SCROLLABLE_OVERFLOW_VALUES.has(getOverflowY(element)) &&
    getMaxScrollTop(element) > SCROLL_EPSILON
  )
}

export const findVerticalScrollableAncestor = (
  element: HTMLElement | null,
): HTMLElement | null => {
  let current = element?.parentElement ?? null

  while (current) {
    if (isVerticallyScrollable(current)) {
      return current
    }
    current = current.parentElement
  }

  return document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement
}
